import React, { useState, useEffect, useRef } from "react";
import { HashLink } from "react-router-hash-link";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import logo from "../../assets/logoPrimary.png";
import LgDropdownMenu from "../../components/languageDropDown/LanguageDropDown.component";
import HomeDrawer from "../HomeDrawer/HomeDrawer.component";
import { scrollWithOffset } from "../../utils/scrollWithOffset";
import { Menu, X, LogIn } from "lucide-react";
import { cn } from "@/lib/utils";
import { useScroll, useMotionValueEvent, motion } from "framer-motion";
import { debounce } from "lodash";

export default function HomeHeader() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [scrollDirection, setScrollDirection] = useState("down");
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isMobileView, setIsMobileView] = useState(false);
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";
  const scrollContainerRef = useRef(null);

  // ✅ Prevent Framer Motion from running before hydration
  const [isReady, setIsReady] = useState(false);
  useEffect(() => setIsReady(true), []);

  useEffect(() => {
    const findScrollContainer = () => {
      const viewport = document.querySelector(
        "[data-radix-scroll-area-viewport]"
      );
      if (viewport) {
        scrollContainerRef.current = viewport;
      } else {
        setTimeout(findScrollContainer, 200);
      }
    };
    findScrollContainer();

    const handleWindowScroll = () => {
      const scrollY = window.scrollY;
      setIsScrolled(scrollY > 20);
    };

    window.addEventListener("scroll", handleWindowScroll);
    return () => {
      window.removeEventListener("scroll", handleWindowScroll);
    };
  }, []);

  // ✅ Only initialize Framer Motion scroll once container is ready
  const { scrollY } = useScroll(
    isReady && scrollContainerRef.current
      ? { container: scrollContainerRef }
      : undefined
  );

  useMotionValueEvent(
    scrollY,
    "change",
    debounce((current) => {
      const previous = scrollY.getPrevious();
      const diff = current - previous;
      const threshold = 20;
      setIsScrolled(current > threshold);
      setScrollDirection(diff > 0 ? "down" : "up");
    }, 100)
  );

  useEffect(() => {
    const checkMobile = () => {
      setIsMobileView(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const navLinks = [
    {
      titleKey: "services",
      href: "/#segments",
      slug: t("homepage.slugs.segments", "segments"),
    },
    {
      titleKey: "features",
      href: "/#platform-features",
      slug: t("homepage.slugs.platformFeatures", "platform-features"),
    },
    {
      titleKey: "useCases",
      href: "/#use-cases",
      slug: t("homepage.slugs.useCases", "use-cases"),
    },
    {
      titleKey: "pricing",
      href: "/#pricing-plans",
      slug: t("homepage.slugs.pricing", "pricing-plans"),
    },
    {
      titleKey: "contact",
      href: "/#contact",
      slug: t("homepage.slugs.contact", "contact"),
    },
  ];

  return (
    <>
      <motion.header
        animate={{
          backgroundColor: isScrolled
            ? "hsl(var(--background))"
            : "hsl(var(--background) / 0)",
          boxShadow: isScrolled
            ? "0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)"
            : "none",
        }}
        transition={{ duration: 0.3 }}
        className={cn(
          "fixed top-0 left-0 right-0 z-[200] transition-colors duration-300 ease-in-out",
          isScrolled
            ? "border-b border-border/20 opacity-100 bg-white bg-opacity-90"
            : "border-b border-transparent",
          "py-3"
        )}
      >
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">
            <div
              className={cn(
                "flex-shrink-0 flex items-center group",
                isRTL ? "ml-auto" : "mr-auto"
              )}
            >
              <HashLink
                smooth
                to="/#"
                scroll={(el) => scrollWithOffset(el, 0)}
                className="flex items-center gap-2"
              >
                <img
                  src={logo}
                  alt={t("common.logoAlt", "Al Saqr Logo")}
                  className="h-8 w-auto transition-all duration-300 group-hover:opacity-80"
                />
                <span
                  className={cn(
                    "font-bold text-xl tracking-tight",
                    isScrolled
                      ? "text-foreground"
                      : "text-primary-foreground md:text-foreground"
                  )}
                >
                  {t("common.platformName", "Al Saqr")}
                </span>
              </HashLink>
            </div>

            {!isMobileView && (
              <nav
                className={cn(
                  "hidden md:flex items-center space-x-0.5 lg:space-x-1",
                  isRTL ? "space-x-reverse" : ""
                )}
              >
                {navLinks.map((link) => (
                  <Button
                    key={link.titleKey}
                    variant="ghost"
                    asChild
                    className={cn(
                      "px-3 py-2 text-sm font-medium transition-colors duration-150 rounded-md",
                      isScrolled
                        ? "text-muted-foreground hover:text-primary hover:bg-primary/10"
                        : "text-primary-foreground/80 hover:text-primary-foreground hover:bg-white/10 md:text-muted-foreground md:hover:text-primary md:hover:bg-primary/10"
                    )}
                  >
                    <HashLink
                      smooth
                      to={`/${
                        i18n.language === "ar" ? "ar/" : ""
                      }#${link.slug}`}
                      scroll={(el) =>
                        scrollWithOffset(el, isScrolled ? 80 : 20)
                      }
                    >
                      {t(`homepage.header.nav.${link.titleKey}`)}
                    </HashLink>
                  </Button>
                ))}
              </nav>
            )}

            <div
              className={cn(
                "flex items-center gap-1.5 sm:gap-2",
                isRTL ? "mr-auto" : "ml-auto"
              )}
            >
              <div className={cn(isMobileView && "hidden", "md:block")}>
                <LgDropdownMenu scrolled={isScrolled} />
              </div>

              {!isMobileView && (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    asChild
                    className={cn(
                      "transition-colors duration-300",
                      isScrolled
                        ? "border-primary/50 text-primary hover:bg-primary/10 hover:border-primary"
                        : "border-primary-foreground/40 text-primary-foreground hover:bg-primary-foreground/10 hover:border-primary-foreground/70 md:border-primary/50 md:text-primary md:hover:bg-primary/10 md:hover:border-primary"
                    )}
                  >
                    <Link to="/login">
                      <LogIn
                        size={14}
                        className={cn(
                          "transition-transform",
                          isRTL ? "ml-1.5" : "mr-1.5"
                        )}
                      />
                      {t("homepage.header.login", "Login")}
                    </Link>
                  </Button>
                  <Button
                    size="sm"
                    asChild
                    className={cn(
                      "transition-colors duration-300",
                      isScrolled
                        ? "bg-primary text-primary-foreground hover:bg-primary/90"
                        : "bg-primary-foreground/10 text-primary-foreground hover:bg-primary-foreground/20 md:bg-primary md:text-primary-foreground md:hover:bg-primary/90"
                    )}
                  >
                    <HashLink
                      smooth
                      to={`/${
                        i18n.language === "ar" ? "ar/" : ""
                      }#${t("homepage.slugs.contact", "contact")}`}
                      scroll={(el) => scrollWithOffset(el, 50)}
                    >
                      {t("homepage.header.ctaContact", "Contact Us")}
                    </HashLink>
                  </Button>
                </>
              )}

              <Button
                variant="ghost"
                size="icon"
                className={cn(
                  "md:hidden transition-colors duration-300 rounded-full w-9 h-9",
                  isScrolled
                    ? "text-foreground hover:bg-muted"
                    : "text-primary-foreground hover:bg-white/10"
                )}
                onClick={() => setIsDrawerOpen(!isDrawerOpen)}
                aria-label={
                  isDrawerOpen
                    ? t("common.closeMenu", "Close menu")
                    : t("common.openMenu", "Open menu")
                }
              >
                {isDrawerOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </Button>
            </div>
          </div>
        </div>
      </motion.header>
      <HomeDrawer
        isOpen={isDrawerOpen}
        setIsOpen={setIsDrawerOpen}
        navLinks={navLinks}
        scrolled={isScrolled}
      />
    </>
  );
}
